import { useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { resolveClassPanel, sortJudgesForDisplay } from "score-common";
import { useTranslation } from "i18n-fe";
import { colors } from "brand";
import { useShowJudges } from "../hooks/useShowJudges";

const BRAND_ACCENT = colors.foxOrange;

// #1751 — the class-level ceremony (docs/score/equitation-class-sheet-design.md
// §6). On an equitation class the whole class is ONE sheet of paper, so
// confirming and signing happen once for the class, not 25 times. A scribe who
// has to run the per-ride confirm-then-sign flow 25 times at the end of a class
// will not do it.
//
// Three ordered stages, each gated on the one before:
//
//   1. Confirm marks over the whole grid — one action, freezing every row at
//      once with #696 semantics. REFUSED while any row is missing a Total
//      (USEF DR133.1.j: every rider competing in the class gets a final
//      percentage). That per-row count is #1750's live `hasTotalByRide`
//      signal, not a re-derivation.
//   2. Each panel judge signs ONCE, and that signature is applied to every
//      row's sheet. DR133.2.g.3 seats 2 judges on a DSM Semi-Final and 3 on a
//      Final, all on the same class score sheet.
//   3. Complete, gated on every assigned judge having signed (#1748's server
//      gate, which 409s a partially-signed sheet). Signed implies confirmed
//      per #696.
//
// PARTIAL CEREMONY is the failure mode this is designed against, and it is
// deliberately REPRESENTABLE rather than prevented: 25 rows times 3 judges is
// 75 independent per-sheet writes through the offline outbox, and a tablet
// that loses its network mid-ceremony WILL land some and not others. So every
// stage below reports its own live "N of M" count, a judge is only shown as
// having signed once EVERY row carries their signature, and the next stage
// stays disabled until the previous one is complete across the whole class.
// The server enforces the same thing independently (#1748), so a half-signed
// class cannot be completed even if this UI were bypassed.
//
// KIOSK DOCTRINE: while a stage is in flight the progress dialog below is
// modal with no dismiss route (no Cancel, no backdrop click, no Escape) and
// the breadcrumb links are suppressed by ClassGrid, so a scribe cannot wander
// out of a half-finished ceremony into another part of the app.

// The brand theme sets palette.primary.main to darkGrey in BOTH the light and
// the dark scheme (packages/brand/lib/ThemeBrand.js), so MUI's default primary
// button is low-contrast against the dark scheme's own surface. The accent
// (foxOrange) is the one brand color that reads in both, and it is already
// what the theme uses for an "on" switch. Disabled state is left to MUI so a
// refused action still looks refused.
const ACCENT_BUTTON_SX = {
  "&:not(.Mui-disabled)": {
    bgcolor: BRAND_ACCENT,
    color: "#1b1b1b",
    "&:hover": { bgcolor: "#d9820f" },
  },
};

export default function ClassCeremony({
  klass,
  test,
  rides,
  missingTotalCount,
  // #3293 - riders never drawn into a section and not yet marked No show or
  // Scratched. Confirming with any outstanding would leave them PENDING, which
  // blocks auto-place forever, so the confirm stage waits (ClassGrid lists them).
  blockedCount = 0,
  isScoredGrid,
  rowStates,
  busyOp,
  onRunCeremony,
}) {
  const { t } = useTranslation("score");
  // #2196 — `loading` is safe to gate on here (unlike SessionHeader's
  // classId, klass?.showId is already resolved by the time this component
  // mounts: ClassGrid only renders ClassCeremony once its own `klass` fetch
  // has settled), so there is no post-mount id-change for #2162's
  // never-resets-loading bug to hit. Without this gate, the hook's own
  // initial `data: null` default read as "confirmed zero judges" via the
  // `?? []` fallback below and rendered noPanelTitle on every mount, even
  // while the very first real fetch was still in flight.
  const { data: showJudges, loading: judgesLoading } = useShowJudges(klass?.showId);
  const [pendingConfirm, setPendingConfirm] = useState(false);

  // The panel's seats in printed-signature-line order, each resolved to the
  // judge ROSTER ROW id that signatures are keyed by (#818). Same pure helper
  // score-api runs, so the UI and the server cannot disagree about who sits
  // where — see resolveClassPanel's own comment for why Class.judgeIds is not
  // a substitute (reconcile sorts it alphabetically; it carries no position).
  const panel = useMemo(
    // #2788: resolveClassPanel already walks JUDGE_POSITIONS, but the render
    // site pins the arena order (E, H, C, M, B, #2786) itself rather than
    // trusting a helper's internal iteration order.
    () =>
      sortJudgesForDisplay(
        resolveClassPanel(klass, showJudges ?? []),
        (seat) => seat.position,
      ),
    [klass, showJudges],
  );

  // #733 — there is no signature pad in scribe: judge signatures are captured
  // once per show in scoreadmin, and signing here ADOPTS the stored one. A
  // judge with nothing on file cannot sign at all, and the fix is a trip to
  // the show secretary, so say that rather than failing at the tap.
  const signaturePresentById = useMemo(() => {
    const map = new Map();
    for (const j of showJudges ?? []) map.set(j.id, Boolean(j.signaturePresent));
    return map;
  }, [showJudges]);

  const rowCount = rides.length;
  const confirmedCount = rides.filter((r) => rowStates[r.id]?.isConfirmed).length;
  const completedCount = rides.filter((r) => rowStates[r.id]?.isCompleted).length;

  // A judge counts as having signed the CLASS only once every row that NEEDS
  // a signature carries theirs. Anything less is a partial signature, and
  // reporting it as "signed" is exactly how a half-signed class would slip
  // through.
  //
  // #2527 — a paperEntry row's sheet is signed in ink and never through this
  // app (same rule the noSignatureNeeded guard below reads off it), so it
  // never contributes a signedJudgeIds entry and must never be counted
  // toward a judge's tally either. Skipping it here is what lets
  // signatureNeededCount (below) work as the accounting denominator instead
  // of rowCount on a MIXED class.
  const signedRowCountByJudge = useMemo(() => {
    const counts = new Map();
    for (const ride of rides) {
      if (rowStates[ride.id]?.noSignatureNeeded === true) continue;
      for (const judgeId of rowStates[ride.id]?.signedJudgeIds ?? []) {
        counts.set(judgeId, (counts.get(judgeId) ?? 0) + 1);
      }
    }
    return counts;
  }, [rides, rowStates]);

  const isConsensus = Boolean(test?.panel?.consensus);
  const allConfirmed = rowCount > 0 && confirmedCount === rowCount;
  const allCompleted = rowCount > 0 && completedCount === rowCount;
  const seatedPanel = panel.filter((seat) => seat.judgeId);
  // #2527 — how many of this class's rows actually need a signature. A
  // fail-closed count on purpose: a row whose sheet has not loaded yet
  // (rowStates[id] === undefined) reads as "!== true", i.e. still needing a
  // signature, same fail-closed direction classNoSignatureNeeded below
  // already took for the all-rows case. On an ordinary (non-paper) class
  // this is just rowCount once every sheet has loaded, so it changes nothing
  // there; on a MIXED class (setClassPaperEntry flipped after some sheets
  // already existed, #2527 card) it is the count that actually gates
  // completion, not the whole class's row count.
  const signatureNeededCount = rides.filter(
    (r) => rowStates[r.id]?.noSignatureNeeded !== true,
  ).length;
  const judgeHasSignedClass = (judgeId) =>
    signatureNeededCount > 0 &&
    (signedRowCountByJudge.get(judgeId) ?? 0) === signatureNeededCount;
  // #2244 — the class-level panel-signature gate applies ONLY to a
  // consensus-panel test. A panel-consensus ride has ONE co-signed sheet
  // (#1763), which is the whole reason this stage exists; every other test
  // gives each judge their own sheet, signed per ride behind the #698 gate,
  // so a non-consensus class has nothing for this stage to wait on. Gating on
  // it anyway made COMPLETE CLASS unreachable forever on any one-sheet class
  // that merely had a judge seated: stage 2 said "no panel signature to
  // collect here" and rendered no sign button, while stage 3 named that judge
  // and stayed disabled — a kiosk dead end with no escape hatch. 91 classes on
  // dev were deadlocked this way, including Friday's Materiale.
  // `outstandingJudges` is held empty off the consensus path for the same
  // reason: stage 3 must never name a judge it never asked anyone to sign for.
  // The seatedPanel.length > 0 term is deliberately kept on BOTH paths — a
  // class with no judge assigned at all still cannot be completed, and
  // completeBlockedNoPanel stays the accurate, actionable message for it.
  // #2516 — a paperEntry class's sheets are stamped noSignatureNeeded once at
  // createScoreSheet (#2406/#2407, apps/score-api/.../scoresheet.js), because
  // the judge signed the paper original in ink and there is no tablet
  // signature step for it anywhere in this app. Nothing in setClassPaperEntry
  // forbids flagging a panel-consensus class this way (#2516 card comment),
  // so a consensus test's judges can legitimately have no show-wide
  // e-signature on file at all. Without this guard the per-seat Sign button
  // stayed permanently disabled (hasSignatureOnFile false) and
  // allJudgesSigned could never clear, dead-ending COMPLETE CLASS on a kiosk
  // with no escape hatch -- the exact #2244 shape, in this same file, for a
  // combination that card's own sweep did not enumerate. Mirrors #2412's
  // identical guard in ScoringForm.jsx's hasMissingSignature: read the flag
  // straight off each row's own sheet (rowStates[].noSignatureNeeded, wired
  // in ClassGridRow.jsx), never re-derived from klass.paperEntry directly.
  // Requires every row to already report it so a mid-fetch tick (a sheet not
  // yet loaded reports rowStates[id] === undefined) can't false-positive; by
  // the time allConfirmed is true every row's sheet has already loaded, which
  // is the only point this flag is read for.
  //
  // #2527 — reads off signatureNeededCount (rows where noSignatureNeeded is
  // NOT true) rather than re-deriving with its own rides.every: the two must
  // stay the complement of each other, or a MIXED class could show the
  // "no signature needed" alert (classNoSignatureNeeded true) while
  // judgeHasSignedClass's denominator (signatureNeededCount) still expected a
  // signature from somewhere, wedging stage 2 into a state neither branch
  // handles.
  const classNoSignatureNeeded = rowCount > 0 && signatureNeededCount === 0;
  const signatureRequired = isConsensus && !classNoSignatureNeeded;
  const outstandingJudges = signatureRequired
    ? seatedPanel.filter((seat) => !judgeHasSignedClass(seat.judgeId))
    : [];
  const allJudgesSigned =
    seatedPanel.length > 0 && (!signatureRequired || outstandingJudges.length === 0);

  // RULED (Jaybill, 2026-09-02, card #2225): a scored grid class (MATERIALE /
  // UNDER_SADDLE) relaxes this gate ENTIRELY — canConfirm no longer looks at
  // missingTotalCount for those classes. A scratched, withdrawn, eliminated,
  // or retired horse renders a row of empty ScoreEntry cells that, on this
  // grid family, has no other way to become non-missing (unlike the
  // equitation grid's typed-Total row, there is no field a scribe can fill
  // for a horse that did not compete), so gating on it would make an
  // ordinary scratch UNCONFIRMABLE — a kiosk with no escape hatch. The
  // missing-scores banner below stays and stays accurate; it now only warns,
  // it never gates. Two alternatives were considered and rejected: filtering
  // by ride.status (can only ever exclude the statuses we thought of, and
  // score-api's batched ride payload was not confirmed to carry a reliable
  // signal here) and a per-row explicit "no score" affordance (its own
  // DR211.5 reporting question, a bigger surface than this fix). Do not
  // restore missingTotalCount to this expression for isScoredGrid without a
  // new ruling.
  const canConfirm =
    rowCount > 0 && !allConfirmed && !busyOp &&
    blockedCount === 0 &&
    (isScoredGrid || missingTotalCount === 0);
  const canComplete = allConfirmed && allJudgesSigned && !allCompleted && !busyOp;

  const runConfirm = () => {
    setPendingConfirm(false);
    onRunCeremony("confirm");
  };

  return (
    <Paper variant="outlined" sx={{ mt: 3, p: 2 }} data-testid="class-ceremony">
      <Typography variant="h6" fontWeight={700}>
        {t("classCeremony.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {isScoredGrid ? t("classCeremony.subtitleDshb") : t("classCeremony.subtitle")}
      </Typography>

      {/* ── Stage 1: confirm the whole grid ──────────────────────────── */}
      <Box sx={{ mt: 2 }} data-testid="ceremony-confirm-stage">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <StageIcon done={allConfirmed} />
          <Typography variant="subtitle1" fontWeight={700}>
            {t("classCeremony.confirmHeading")}
          </Typography>
          <Chip
            size="small"
            data-testid="ceremony-confirmed-count"
            color={allConfirmed ? "success" : "default"}
            label={t("classCeremony.rowCount", { count: confirmedCount, total: rowCount })}
          />
        </Stack>

        {allConfirmed ? (
          // #696 — one-way. There is no un-confirm control here because there
          // is no un-confirm route anywhere in the API.
          <Alert severity="success" data-testid="ceremony-confirmed-alert">
            {t("classCeremony.confirmedIrreversible")}
          </Alert>
        ) : (
          <>
            {missingTotalCount > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }} data-testid="ceremony-confirm-blocked">
                {/* #2235 — DR211.5 (docs/rules/usef/rulebook-dr-dressage-2026.pdf,
                    labelled page DR-74) has TWO separate clauses: scores are
                    reported as a total against a possible 100%, and
                    SEPARATELY "all scores (if given) and placings must be
                    published". Do not weld them into "placings published as
                    a percentage" - placings are not percentages. Not that
                    every rider must receive a score. Keep this key in sync
                    with classGrid.missingTotalBannerDshb, a byte-identical
                    copy from #2225. */}
                {isScoredGrid
                  ? t("classCeremony.missingScoresWarningDshb", {
                      count: missingTotalCount,
                      total: rowCount,
                    })
                  : t("classCeremony.confirmBlockedByTotals", {
                      count: missingTotalCount,
                      total: rowCount,
                    })}
              </Alert>
            )}
            {confirmedCount > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }} data-testid="ceremony-partial-confirm">
                {t("classCeremony.partiallyConfirmed", {
                  count: confirmedCount,
                  total: rowCount,
                })}
              </Alert>
            )}
            <Button
              variant="contained"
              // Same reason as the Sign button below: a `contained` primary is
              // the theme's darkGrey, which all but disappears against the
              // dark scheme's own surface. The brand accent reads in both.
              sx={ACCENT_BUTTON_SX}
              disabled={!canConfirm}
              onClick={() => setPendingConfirm(true)}
              data-testid="ceremony-confirm-button"
            >
              {t("classCeremony.confirmAction")}
            </Button>
          </>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* ── Stage 2: each panel judge signs once ─────────────────────── */}
      <Box data-testid="ceremony-sign-stage">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <StageIcon done={allJudgesSigned} />
          <Typography variant="subtitle1" fontWeight={700}>
            {t("classCeremony.signHeading")}
          </Typography>
        </Stack>

        {!isConsensus ? (
          <Alert severity="info" data-testid="ceremony-no-panel">
            {t("classCeremony.notAPanelTest")}
          </Alert>
        ) : classNoSignatureNeeded ? (
          // #2516 — a paper-scored consensus class was signed in ink on the
          // paper original; there is no show-wide e-signature to adopt and no
          // per-seat Sign action to offer, same reasoning as ScoringForm.jsx's
          // suppressed SignatureBox (#2412). Showing the per-judge list here
          // with a permanently disabled button and a red "no signature on
          // file" error is exactly the dead end this card exists to close.
          <Alert severity="info" data-testid="ceremony-no-signature-needed">
            {t("classCeremony.noSignatureNeeded")}
          </Alert>
        ) : judgesLoading ? (
          // #2196 — distinct testid from ceremony-no-judges (never share one
          // across two render branches, see #2197): this only shows while
          // useShowJudges is genuinely still fetching, never once it has
          // settled to a real "no judges assigned" answer.
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            data-testid="ceremony-judges-loading"
          >
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t("classCeremony.loadingPanel")}
            </Typography>
          </Stack>
        ) : seatedPanel.length === 0 ? (
          <Alert severity="warning" data-testid="ceremony-no-judges">
            <AlertTitle>{t("classCeremony.noPanelTitle")}</AlertTitle>
            {t("classCeremony.noPanelBody")}
          </Alert>
        ) : (
          <Stack spacing={1}>
            {panel.map((seat) => {
              const signedRows = seat.judgeId
                ? signedRowCountByJudge.get(seat.judgeId) ?? 0
                : 0;
              const fullySigned = seat.judgeId ? judgeHasSignedClass(seat.judgeId) : false;
              const hasSignatureOnFile = seat.judgeId
                ? signaturePresentById.get(seat.judgeId) === true
                : false;
              return (
                <Stack
                  key={seat.position}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  data-testid={`ceremony-judge-${seat.position}`}
                  data-signed={fullySigned ? "true" : "false"}
                >
                  <StageIcon done={fullySigned} />
                  <Box sx={{ minWidth: 220 }}>
                    <Typography variant="body1" fontWeight={600}>
                      {seat.judgeName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("classCeremony.seatLabel", { position: seat.position })}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    color={fullySigned ? "success" : signedRows > 0 ? "warning" : "default"}
                    data-testid={`ceremony-judge-${seat.position}-count`}
                    label={
                      fullySigned
                        ? t("classCeremony.judgeSigned")
                        : t("classCeremony.rowCount", {
                            count: signedRows,
                            // #2527 — denominate against the rows that need a
                            // signature, not the whole class, so a MIXED
                            // class's Chip reads "1 of 1" once the one
                            // signature-needed row is signed rather than a
                            // permanently-short "1 of 2".
                            total: signatureNeededCount,
                          })
                    }
                  />
                  {!fullySigned && (
                    <Button
                      size="small"
                      variant="outlined"
                      // NOT color="primary": the brand theme sets
                      // palette.primary.main to darkGrey in BOTH schemes
                      // (packages/brand/lib/ThemeBrand.js), so an outlined
                      // primary button is dark grey on a dark background and
                      // effectively invisible in dark mode. Caught by the
                      // light/dark capture, not by Cypress. Paint it with the
                      // text colors, which the theme does flip.
                      sx={{
                        color: "text.primary",
                        borderColor: "text.secondary",
                        "&:hover": { borderColor: "text.primary" },
                      }}
                      data-testid={`ceremony-sign-${seat.position}`}
                      // A stuck ceremony is the thing that will get reported
                      // from a show as "the Sign button does nothing", so say
                      // WHY it is disabled somewhere a test (and a console)
                      // can read without guessing.
                      data-disabled-reason={
                        !allConfirmed
                          ? "not-confirmed"
                          : !seat.judgeId
                            ? "judge-unresolved"
                            : !hasSignatureOnFile
                              ? "no-signature-on-file"
                              : busyOp
                                ? "busy"
                                : ""
                      }
                      disabled={
                        !allConfirmed || !seat.judgeId || !hasSignatureOnFile || Boolean(busyOp)
                      }
                      onClick={() => onRunCeremony("sign", seat.judgeId)}
                    >
                      {signedRows > 0
                        ? t("classCeremony.signActionResume")
                        : t("classCeremony.signAction")}
                    </Button>
                  )}
                  {!hasSignatureOnFile && seat.judgeId && (
                    <Typography variant="caption" color="error" data-testid={`ceremony-no-sig-${seat.position}`}>
                      {t("classCeremony.judgeHasNoSignature")}
                    </Typography>
                  )}
                  {!seat.judgeId && (
                    <Typography variant="caption" color="error">
                      {t("classCeremony.judgeUnresolved")}
                    </Typography>
                  )}
                </Stack>
              );
            })}
            {!allConfirmed && (
              <Typography variant="caption" color="text.secondary" data-testid="ceremony-sign-locked">
                {t("classCeremony.signLockedUntilConfirmed")}
              </Typography>
            )}
          </Stack>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* ── Stage 3: complete ────────────────────────────────────────── */}
      <Box data-testid="ceremony-complete-stage">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <StageIcon done={allCompleted} />
          <Typography variant="subtitle1" fontWeight={700}>
            {t("classCeremony.completeHeading")}
          </Typography>
          <Chip
            size="small"
            data-testid="ceremony-completed-count"
            color={allCompleted ? "success" : "default"}
            label={t("classCeremony.rowCount", { count: completedCount, total: rowCount })}
          />
        </Stack>

        {allCompleted ? (
          <Alert severity="success" data-testid="ceremony-complete-done">
            {t("classCeremony.completed")}
          </Alert>
        ) : (
          <>
            {/* The refusal is stated, not merely implied by a greyed button:
                a scribe standing in front of a judge needs to know WHO is
                still outstanding, because that judge is probably still in the
                booth. */}
            {!canComplete && (
              <Alert severity="info" sx={{ mb: 1 }} data-testid="ceremony-complete-blocked">
                {!allConfirmed
                  ? t("classCeremony.completeBlockedUnconfirmed")
                  : outstandingJudges.length > 0
                    ? t("classCeremony.completeBlockedUnsigned", {
                        judges: outstandingJudges.map((s) => s.judgeName).join(", "),
                      })
                    : t("classCeremony.completeBlockedNoPanel")}
              </Alert>
            )}
            <Button
              variant="contained"
              sx={ACCENT_BUTTON_SX}
              disabled={!canComplete}
              onClick={() => onRunCeremony("complete")}
              data-testid="ceremony-complete-button"
            >
              {t("classCeremony.completeAction")}
            </Button>
          </>
        )}
      </Box>

      {/* Irreversible-freeze gate, the same shape as the per-ride #698 dialog. */}
      <Dialog open={pendingConfirm} onClose={() => setPendingConfirm(false)}>
        <DialogTitle>{t("classCeremony.confirmDialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("classCeremony.confirmDialogBody", { count: rowCount })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingConfirm(false)}>
            {t("classCeremony.cancel")}
          </Button>
          <Button
            variant="contained"
            sx={ACCENT_BUTTON_SX}
            onClick={runConfirm}
            data-testid="ceremony-confirm-dialog-ok"
          >
            {t("classCeremony.confirmDialogAction")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* KIOSK: no Cancel, no backdrop dismiss, no Escape. A ceremony in
          flight is 25-75 queued per-sheet writes; letting the scribe navigate
          away mid-fan-out is exactly the partial state this card exists to
          prevent people from creating by accident.

          What actually guarantees that is `open` being derived from the
          in-flight op with a no-op onClose: MUI reports Escape and backdrop
          clicks through onClose, and there is nothing there to close it with.
          `disableEscapeKeyDown` is belt-and-braces on top of that and is, on
          its own, redundant — measured, not assumed (mutation C9 in the #1751
          table: removing it changes nothing observable). Do not read it as the
          mechanism, and do not "test" it: see class-ceremony.cy.js's AC6 test
          for what is asserted instead and why. */}
      <Dialog
        open={Boolean(busyOp)}
        disableEscapeKeyDown
        onClose={() => {}}
        data-testid="ceremony-busy-dialog"
      >
        {/* Static literal keys only — a template-built key is invisible to the
            i18n extractor and silently ships an untranslated string. */}
        <DialogTitle>
          {busyOp?.kind === "sign"
            ? t("classCeremony.busySign")
            : busyOp?.kind === "complete"
              ? t("classCeremony.busyComplete")
              : t("classCeremony.busyConfirm")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("classCeremony.busyBody", { count: rowCount })}
          </DialogContentText>
          <LinearProgress
            variant="determinate"
            value={rowCount > 0 ? (100 * (busyOp?.done ?? 0)) / rowCount : 0}
          />
          <Typography variant="caption" color="text.secondary" data-testid="ceremony-busy-progress">
            {t("classCeremony.rowCount", { count: busyOp?.done ?? 0, total: rowCount })}
          </Typography>
        </DialogContent>
      </Dialog>
    </Paper>
  );
}

function StageIcon({ done }) {
  return done ? (
    <CheckCircleIcon color="success" fontSize="small" />
  ) : (
    <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
  );
}
